import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { X } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import '../styles/inspire.css';
import '../styles/inspire-home.css';
import '../styles/service-page.css';

const ReferAFamily = () => {
  const [formData, setFormData] = useState({
    referrerName: '',
    organization: '',
    referrerEmail: '',
    referrerPhone: '',
    familyContact: '',
    reason: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/referral`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, source: 'partner' })
      });
      if (!response.ok) throw new Error('Failed to submit');
      setIsSubmitted(true);
      setShowModal(true);
      setFormData({ referrerName: '', organization: '', referrerEmail: '', referrerPhone: '', familyContact: '', reason: '' });
    } catch (error) {
      console.error('Error submitting referral:', error);
      alert('There was an error submitting your referral. Please try again.');
    }
  };

  return (
    <div className="therapy-site" data-testid="refer-a-family-page">
      <Helmet>
        <title>Refer a Family | Inspire Therapy Collective - Orlando, FL</title>
        <meta name="description" content="Refer a family to Inspire Therapy Collective. For school counselors, pediatricians, and clinicians supporting children, teens, and women in need of therapy." />
      </Helmet>

      <Header />

      <section className="service-hero">
        <div className="service-hero-gradient" />
        <div className="service-hero-content fade-in-up">
          <h1 className="gradient-text" data-testid="refer-page-title">Refer a Family</h1>
          <p className="service-tagline">For school counselors, pediatricians, and clinicians supporting families.</p>
        </div>
      </section>

      <section className="refer-intro-section">
        <div className="container">
          <div className="refer-intro-content fade-in-up">
            <p>
              If you work with children, teens, or women who could benefit from therapy — whether for anxiety, behavioral challenges, school advocacy, IEP/504 support, or life transitions — I'd love to hear from you.
            </p>
            <p>
              I specialize in therapy for children, teens, and women, with deep experience in school-based advocacy, neurodivergent care, and family support across home, school, and clinical settings.
            </p>
            <p>
              Submit a referral below and I'll follow up within 1 business day. I'll close the loop with you once the family has been contacted and booked.
            </p>
          </div>
        </div>
      </section>

      <section className="refer-form-section">
        <div className="container">
          <div className="refer-form-wrapper fade-in-up">
            <h2 className="heading-2 font-serif gradient-text">Referral Form</h2>
            <form onSubmit={handleSubmit} className="refer-form" data-testid="referral-form">
              <div className="form-group">
                <label htmlFor="referrerName" className="form-label">Your Name *</label>
                <input
                  type="text"
                  id="referrerName"
                  name="referrerName"
                  value={formData.referrerName}
                  onChange={handleChange}
                  required
                  className="form-input"
                  data-testid="referrer-name-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="organization" className="form-label">Organization / Practice *</label>
                <input
                  type="text"
                  id="organization"
                  name="organization"
                  value={formData.organization}
                  onChange={handleChange}
                  required
                  className="form-input"
                  data-testid="organization-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="referrerEmail" className="form-label">Your Email *</label>
                <input
                  type="email"
                  id="referrerEmail"
                  name="referrerEmail"
                  value={formData.referrerEmail}
                  onChange={handleChange}
                  required
                  className="form-input"
                  data-testid="referrer-email-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="referrerPhone" className="form-label">Your Phone (optional)</label>
                <input
                  type="tel"
                  id="referrerPhone"
                  name="referrerPhone"
                  value={formData.referrerPhone}
                  onChange={handleChange}
                  className="form-input"
                  data-testid="referrer-phone-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="familyContact" className="form-label">Family Contact Info *</label>
                <input
                  type="text"
                  id="familyContact"
                  name="familyContact"
                  value={formData.familyContact}
                  onChange={handleChange}
                  required
                  placeholder="Name, phone, or email of the family"
                  className="form-input"
                  data-testid="family-contact-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="reason" className="form-label">Reason for Referral *</label>
                <textarea
                  id="reason"
                  name="reason"
                  value={formData.reason}
                  onChange={handleChange}
                  required
                  rows={4}
                  placeholder="Brief description of the family's needs or presenting concerns"
                  className="form-textarea"
                  data-testid="reason-textarea"
                />
              </div>

              <button type="submit" className="btn btn-primary" data-testid="referral-submit-btn">
                Submit Referral
              </button>
            </form>
          </div>
        </div>
      </section>

      <Footer />

      {showModal && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); setIsSubmitted(false); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => { setShowModal(false); setIsSubmitted(false); }} aria-label="Close">
              <X size={24} />
            </button>
            <div className="modal-body">
              <h2 className="modal-title">Referral Received</h2>
              <p className="modal-text">
                Thank you for referring a family. I'll follow up within 1 business day and close the loop with you once the family has been contacted and booked.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferAFamily;
